import jsPDF from "jspdf";

interface Event {
  title: string;
  start: string;
  end: string;
  location: string;
  populations: string[];
  description: string;
}

interface Season {
  startYear: number;
  endYear: number;
}

interface EventsData {
  season: Season;
  events: Event[];
}

interface CalendarCell {
  month: string;
  weekend: number;
  events: Event[];
  dates?: string;
}

export class DirectPDFGenerator {
  private static readonly PAGE_WIDTH = 841.89; // A4 landscape width in points
  private static readonly PAGE_HEIGHT = 595.28; // A4 landscape height in points
  private static readonly MARGIN = 40;
  private static readonly HEADER_HEIGHT = 80;
  private static readonly FOOTER_HEIGHT = 25;

  private static season: Season | null = null;
  
  // Couleurs
  private static readonly COLORS = {
    primary: '#28a745',
    secondary: '#666666',
    border: '#dee2e6',
    background: '#f8f9fa',
    text: '#333333',
    white: '#ffffff'
  };

  // Dimensions du calendrier optimisées
  private static readonly CALENDAR = {
    startX: this.MARGIN,
    startY: this.MARGIN + this.HEADER_HEIGHT,
    width: this.PAGE_WIDTH - (2 * this.MARGIN),
    height: this.PAGE_HEIGHT - this.HEADER_HEIGHT - this.FOOTER_HEIGHT - (2 * this.MARGIN),
    cols: 11, // 1 colonne pour les week-ends + 10 mois
    rows: 6,  // 1 ligne header + 5 week-ends
    headerRowHeight: 30, // Hauteur augmentée pour les en-têtes
    weekendColWidth: 35, // Largeur augmentée pour la colonne week-ends
  };



  private static getSeason(): Season {
    if (this.season) return this.season;
    const fallbackYear = new Date().getFullYear();
    return { startYear: fallbackYear, endYear: fallbackYear + 1 };
  }

  private static formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: '2-digit' 
    });
  }

  private static async getCalendarData(): Promise<CalendarCell[][]> {
    // Récupérer les vraies données depuis le JSON
    const eventsData = await this.getEventsFromJSON();
    
    const months = [
      'Septembre', 'Octobre', 'Novembre', 'Décembre',
      'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin'
    ];
    
    const weekends = [
      '1er Week end du mois',
      '2e Week end du mois', 
      '3e Week end du mois',
      '4e Week end du mois',
      '5e Week end du mois'
    ];

    const data: CalendarCell[][] = [];
    
    // Ligne d'en-tête avec les mois
    const headerRow: CalendarCell[] = [
      { month: '', weekend: 0, events: [] }, // Cellule vide
      ...months.map(month => ({ month, weekend: 0, events: [] }))
    ];
    data.push(headerRow);

    // Lignes des week-ends
    weekends.forEach((weekend, idx) => {
      const row: CalendarCell[] = [
        { month: weekend, weekend: idx + 1, events: [] }, // Colonne des week-ends
        ...months.map(month => { 
          const dates = this.getWeekendDates(month, idx + 1);
          const events = this.getEventsForMonthAndWeekend(eventsData, month, idx + 1);
          

          
          return { 
            month, 
            weekend: idx + 1, 
            events,
            dates
          };
        })
      ];
      data.push(row);
    });

    return data;
  }

  private static async getEventsFromJSON(): Promise<Event[]> {
    try {
      console.log('DirectPDFGenerator: Chargement des événements depuis events.json...');
      
      // Charger le fichier JSON
      const response = await fetch('/events.json');
      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
      
      const data: EventsData = await response.json();
      console.log('DirectPDFGenerator: Événements chargés depuis JSON:', data.events.length);

      this.season = data.season;
      return data.events;
    } catch (error) {
      console.error('Erreur lors du chargement des événements depuis JSON:', error);
      
      // Retourner un tableau vide si JSON échoue
      return [];
    }
  }





  private static getEventsForMonthAndWeekend(events: Event[], month: string, weekend: number): Event[] {
    // Filtrer les événements pour ce mois et ce week-end
    const monthIndex = this.getMonthIndex(month);
    if (monthIndex === -1) return [];
    
    // Calculer les dates de début et fin du week-end
    const weekendDates = this.getWeekendDateRange(monthIndex, weekend);
    
    return events.filter(event => {
      const eventStart = new Date(event.start);
      const eventEnd = new Date(event.end);
      
      // Pour les événements multi-mois, vérifier si l'événement commence ou se termine dans ce mois
      const eventStartMonth = eventStart.getMonth();
      const eventEndMonth = eventEnd.getMonth();
      
      // L'événement doit soit commencer soit se terminer dans ce mois
      const eventInThisMonth = eventStartMonth === monthIndex || eventEndMonth === monthIndex;
      
      if (!eventInThisMonth) return false;
      
      // Vérifier si l'événement se déroule pendant ce week-end
      const eventOverlapsWeekend = eventStart <= weekendDates.end && eventEnd >= weekendDates.start;
      
      // Pour les événements multi-mois, s'assurer qu'ils apparaissent dans les bonnes cellules
      if (eventStartMonth !== eventEndMonth) {
        // Si l'événement commence dans ce mois, il doit apparaître dans le dernier week-end du mois
        if (eventStartMonth === monthIndex) {
          // Vérifier si c'est le week-end où l'événement commence
          const eventStartWeekend = this.getWeekendForDate(eventStart);
          return eventStartWeekend === weekend;
        }
        // Si l'événement se termine dans ce mois, il doit apparaître dans le premier week-end du mois
        if (eventEndMonth === monthIndex) {
          // Vérifier si c'est le week-end où l'événement se termine
          const eventEndWeekend = this.getWeekendForDate(eventEnd);
          return eventEndWeekend === weekend;
        }
      }
      
      return eventOverlapsWeekend;
    });
  }

  private static getWeekendForDate(date: Date): number {
    // Déterminer dans quel week-end du mois tombe cette date
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    
    // Trouver tous les samedis du mois
    const saturdays: number[] = [];
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    for (let d = 1; d <= daysInMonth; d++) {
      const testDate = new Date(year, month, d);
      if (testDate.getDay() === 6) { // Samedi
        saturdays.push(d);
      }
    }
    
    // Déterminer dans quel week-end tombe cette date
    for (let i = 0; i < saturdays.length; i++) {
      const saturday = saturdays[i];
      const sunday = saturday + 1;
      
      // Si la date est un samedi ou le dimanche suivant
      if (day === saturday || (day === sunday && sunday <= daysInMonth)) {
        return i + 1; // Week-end numéroté à partir de 1
      }
    }
    
    // Si ce n'est pas un week-end, retourner le week-end le plus proche
    for (let i = 0; i < saturdays.length; i++) {
      const saturday = saturdays[i];
      if (day <= saturday) {
        return i + 1;
      }
    }
    
    // Par défaut, dernier week-end du mois
    return saturdays.length;
  }

  private static getMonthIndex(monthName: string): number {
    // Nettoyer la chaîne (supprimer espaces, caractères invisibles)
    const cleanMonth = monthName.trim();
    
    // Mapping principal
    const monthMap: { [key: string]: number } = {
      'Septembre': 8,
      'Octobre': 9,
      'Novembre': 10,
      'Décembre': 11,
      'Janvier': 0,
      'Février': 1,
      'Mars': 2,
      'Avril': 3,
      'Mai': 4,
      'Juin': 5
    };
    
    // Essayer d'abord avec la chaîne nettoyée
    if (monthMap[cleanMonth] !== undefined) {
      return monthMap[cleanMonth];
    }
    
    // Fallback: essayer avec une comparaison insensible à la casse
    const lowerMonth = cleanMonth.toLowerCase();
    for (const [key, value] of Object.entries(monthMap)) {
      if (key.toLowerCase() === lowerMonth) {
        return value;
      }
    }
    

    
    return -1;
  }

  private static getWeekendDateRange(monthIndex: number, weekend: number): { start: Date, end: Date } {
    // Calculer les vraies dates du week-end
    const season = this.getSeason();
    const year = monthIndex >= 8 ? season.startYear : season.endYear;
    const weekendDays = this.getWeekendDatesForMonth(year, monthIndex, weekend);
    
    if (weekendDays.length === 0) {
      // Fallback si pas de week-end trouvé
      return { 
        start: new Date(year, monthIndex, 1), 
        end: new Date(year, monthIndex, 1) 
      };
    }
    
    const startDate = new Date(year, monthIndex, weekendDays[0]);
    const endDate = new Date(year, monthIndex, weekendDays[weekendDays.length - 1]);
    
    return { start: startDate, end: endDate };
  }

  private static getWeekendDates(month: string, weekend: number): string {
    // Calculer les vraies dates des week-ends
    const monthIndex = this.getMonthIndex(month);
    
    if (monthIndex === -1) return '';

    const season = this.getSeason();
    const year = monthIndex >= 8 ? season.startYear : season.endYear;

    // Utiliser la même logique que dans le composant web
    const weekendDays = this.getWeekendDatesForMonth(year, monthIndex, weekend);
    
    if (weekendDays.length === 0) return '';
    
    const shortMonth = month.slice(0, 3);
    return `${weekendDays.join(" - ")} ${shortMonth}`;
  }

  private static getWeekendDatesForMonth(year: number, monthIndex: number, weekend: number): number[] {
    // Reproduire la logique du composant web pour calculer les dates de week-end
    const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
    
    // Trouver tous les samedis du mois
    const saturdays: number[] = [];
    
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, monthIndex, day);
      const dayOfWeek = date.getDay();
      
      // 6 = Samedi
      if (dayOfWeek === 6) {
        saturdays.push(day);
      }
    }
    
    // Pour chaque samedi, créer un week-end (samedi + dimanche si le dimanche est dans le mois)
    if (weekend <= saturdays.length) {
      const saturday = saturdays[weekend - 1];
      const sunday = saturday + 1;
      
      // Vérifier si le dimanche est dans le même mois
      if (sunday <= daysInMonth) {
        return [saturday, sunday];
      } else {
        return [saturday]; // Seulement le samedi si dimanche est le mois suivant
      }
    }
    
    return [];
  }

  private static async drawHeader(pdf: jsPDF): Promise<void> {
    const centerX = this.PAGE_WIDTH / 2;
    
    // Logo Union (à gauche) - Image PNG
    await this.drawImageLogo(pdf, '/logo-union-vert-white.png', this.MARGIN + 10, this.MARGIN - 20, 60, 90);
    
    // Logo Vovinam (à droite) - Image PNG
    await this.drawImageLogo(pdf, '/logo-vovinam.png', this.PAGE_WIDTH - this.MARGIN - 80, this.MARGIN - 20, 90, 90);
    
    // Titre principal
    pdf.setFontSize(24);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(this.COLORS.primary);
    pdf.text('Calendrier de l\'Union Vovinam Viet Vo Dao France', centerX, this.MARGIN + 25, { align: 'center' });
    
    // Sous-titre
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(this.COLORS.secondary);
    const season = this.getSeason();
    pdf.text(`Saison sportive ${season.startYear}-${season.endYear}`, centerX, this.MARGIN + 45, { align: 'center' });
    

    
    // Ligne de séparation
    pdf.setDrawColor(this.COLORS.primary);
    pdf.setLineWidth(2);
    pdf.line(this.MARGIN, this.MARGIN + this.HEADER_HEIGHT - 10, this.PAGE_WIDTH - this.MARGIN, this.MARGIN + this.HEADER_HEIGHT - 10);
  }

  private static drawCalendarGrid(pdf: jsPDF, data: CalendarCell[][]): void {
    // Calcul des dimensions optimisées
    const monthColWidth = (this.CALENDAR.width - this.CALENDAR.weekendColWidth) / 10; // 10 mois
    const headerRowHeight = this.CALENDAR.headerRowHeight;
    const dataRowHeight = Math.max(80, (this.CALENDAR.height - headerRowHeight) / 5); // Minimum 80px pour un événement par cellule
    
    pdf.setDrawColor(this.COLORS.border);
    pdf.setLineWidth(1);
    
    // Dessiner les lignes horizontales
    for (let row = 0; row <= this.CALENDAR.rows; row++) {
      const y = this.CALENDAR.startY + (row === 0 ? 0 : headerRowHeight + (row - 1) * dataRowHeight);
      pdf.line(this.CALENDAR.startX, y, this.CALENDAR.startX + this.CALENDAR.width, y);
    }
    
    // Dessiner les lignes verticales
    // Première colonne (week-ends)
    pdf.line(this.CALENDAR.startX, this.CALENDAR.startY, this.CALENDAR.startX, this.CALENDAR.startY + this.CALENDAR.height);
    pdf.line(this.CALENDAR.startX + this.CALENDAR.weekendColWidth, this.CALENDAR.startY, this.CALENDAR.startX + this.CALENDAR.weekendColWidth, this.CALENDAR.startY + this.CALENDAR.height);
    
    // Colonnes des mois
    for (let col = 1; col <= 10; col++) {
      const x = this.CALENDAR.startX + this.CALENDAR.weekendColWidth + (col * monthColWidth);
      pdf.line(x, this.CALENDAR.startY, x, this.CALENDAR.startY + this.CALENDAR.height);
    }
    
    // Remplir les cellules avec les nouvelles dimensions
    data.forEach((row, rowIndex) => {
      row.forEach((cell, colIndex) => {
        const cellWidth = colIndex === 0 ? this.CALENDAR.weekendColWidth : monthColWidth;
        const cellHeight = rowIndex === 0 ? headerRowHeight : dataRowHeight;
        this.drawOptimizedCell(pdf, cell, colIndex, rowIndex, cellWidth, cellHeight);
      });
    });
  }

  private static drawOptimizedCell(pdf: jsPDF, cell: CalendarCell, col: number, row: number, cellWidth: number, cellHeight: number): void {
    // Calcul de la position avec les nouvelles dimensions
    const x = col === 0 ? this.CALENDAR.startX : this.CALENDAR.startX + this.CALENDAR.weekendColWidth + ((col - 1) * cellWidth);
    const y = row === 0 ? this.CALENDAR.startY : this.CALENDAR.startY + this.CALENDAR.headerRowHeight + ((row - 1) * cellHeight);
    
    // Couleur de fond pour les en-têtes
    if (row === 0 || col === 0) {
      pdf.setFillColor(this.COLORS.primary);
      pdf.rect(x, y, cellWidth, cellHeight, 'F');
      
      // Texte en blanc pour les en-têtes avec taille réduite
      pdf.setTextColor(this.COLORS.white);
      pdf.setFont('helvetica', 'bold');
      
      if (cell.month) {
        if (row === 0) {
          // En-têtes des mois - taille réduite
          pdf.setFontSize(8);
          const text = cell.month;
          const textWidth = pdf.getTextWidth(text);
          const textX = x + (cellWidth - textWidth) / 2;
          const textY = y + (cellHeight + 8) / 2;
          pdf.text(text, textX, textY);
        } else {
          // En-têtes des week-ends - taille très réduite et texte vertical si nécessaire
          pdf.setFontSize(7);
          const text = cell.month;
          // Texte sur plusieurs lignes si trop long
          const words = text.split(' ');
          let currentY = y + 10;
          words.forEach(word => {
            const textWidth = pdf.getTextWidth(word);
            const textX = x + (cellWidth - textWidth) / 2;
            pdf.text(word, textX, currentY);
            currentY += 8;
          });
        }
      }
    } else {
      // Texte normal pour les cellules de données
      pdf.setTextColor(this.COLORS.text);
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'normal');
      
      // Texte de la cellule pour les dates
      if (cell.dates) {
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(this.COLORS.secondary);
        
        pdf.text(cell.dates, x + 3, y + 12, { maxWidth: cellWidth - 6 });
      }
    }
    
    // Dessiner les événements dans la cellule
    if (cell.events.length > 0) {
      this.drawEvents(pdf, cell.events, x, y, cellWidth, cellHeight);
    }
  }



  private static drawEvents(pdf: jsPDF, events: Event[], x: number, y: number, width: number, height: number): void {
    let eventY = y + 20; // Commencer plus bas pour laisser de l'espace
    
    events.forEach((event, index) => {
      // Hauteur fixe pour chaque événement
      const eventHeight = 50; // Hauteur fixe pour éviter le chevauchement
      if (eventY + eventHeight > y + height - 10) return; // Ne pas déborder de la cellule
      
      // Fond de l'événement - plus grand
      pdf.setFillColor(this.COLORS.background);
      pdf.rect(x + 1, eventY, width - 2, eventHeight, 'F');
      
      // Bordure de l'événement
      pdf.setDrawColor(this.COLORS.border);
      pdf.setLineWidth(0.5);
      pdf.rect(x + 1, eventY, width - 2, eventHeight);
      
      let currentY = eventY + 8;
      
      // Titre de l'événement - 2 lignes avec hauteur fixe
      pdf.setTextColor(this.COLORS.text);
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'bold');
      
      // Diviser le titre en mots et afficher sur 2 lignes maximum
      const words = event.title.split(' ');
      const line1 = words.slice(0, Math.ceil(words.length / 2)).join(' ');
      const line2 = words.slice(Math.ceil(words.length / 2)).join(' ');
      
      pdf.text(line1, x + 3, currentY, { maxWidth: width - 6 });
      currentY += 10;
      
      if (line2.trim()) {
        pdf.text(line2, x + 3, currentY, { maxWidth: width - 6 });
        currentY += 10;
      } else {
        currentY += 5; // Espace si pas de deuxième ligne
      }
      
      // Date de l'événement - hauteur fixe
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(this.COLORS.secondary);
      const dateText = event.start === event.end ? 
        this.formatDate(event.start) : 
        `${this.formatDate(event.start)} - ${this.formatDate(event.end)}`;
      pdf.text(dateText, x + 3, currentY, { maxWidth: width - 6 });
      currentY += 8;
      
      // Localisation - hauteur fixe
      if (event.location && event.location.trim()) {
        pdf.setFontSize(6);
        pdf.setTextColor(this.COLORS.secondary);
        pdf.text(event.location, x + 3, currentY, { maxWidth: width - 6 });
        currentY += 7;
      } else {
        currentY += 7; // Espace réservé même si pas de localisation
      }
      
      // Populations avec icônes - hauteur fixe
      if (event.populations.length === 0) {
        // Toutes ceintures - badge vert
        pdf.setFillColor(this.COLORS.primary);
        pdf.rect(x + 3, currentY - 3.5, width - 8, 6, 'F');
        pdf.setTextColor(this.COLORS.white);
        pdf.setFontSize(5);
        pdf.setFont('helvetica', 'bold');
        const text = 'Toutes ceintures';
        const textWidth = pdf.getTextWidth(text);
        const textX = x + 3 + (width - 8 - textWidth) / 2;
        pdf.text(text, textX, currentY + 1);
      } else {
        // Dessiner les icônes de ceintures
        this.drawBeltIcons(pdf, event.populations, x + 3, currentY - 3, width - 6);
      }
      
      eventY += eventHeight + 3; // Espacement fixe entre événements
    });
  }

  private static drawBeltIcons(pdf: jsPDF, populations: string[], x: number, y: number, maxWidth: number): void {
    const iconWidth = 12; // Largeur des icônes SVG (32px / 2 pour le PDF)
    const iconHeight = 6; // Hauteur des icônes SVG (12px / 2 pour le PDF)
    const iconSpacing = 2;
    let currentX = x;
    
    populations.forEach((population, index) => {
      // Vérifier qu'on ne dépasse pas la largeur disponible
      //if (currentX + iconWidth > x + maxWidth) return;
      
      // Dessiner l'icône de ceinture fidèle au SVG
      this.drawBeltIcon(pdf, population, currentX, y, iconWidth, iconHeight);
      
      currentX += iconWidth + iconSpacing;
    });
  }

  private static drawBeltIcon(pdf: jsPDF, population: string, x: number, y: number, width: number, height: number): void {
    // Couleurs des ceintures fidèles aux SVG
    const beltColors: { [key: string]: { fill: string, stroke: string, bands: number[], bandColor: string } } = {
      'CN': { fill: '#111111', stroke: '#222222', bands: [], bandColor: '#FFD700' }, // Ceinture noire
      'CB00': { fill: '#0033a0', stroke: '#222222', bands: [], bandColor: '#FFD700' }, // Ceinture bleue
      'CB01': { fill: '#0033a0', stroke: '#222222', bands: [7], bandColor: '#FFD700' }, // 1 bande
      'CB02': { fill: '#0033a0', stroke: '#222222', bands: [7, 15], bandColor: '#FFD700' }, // 2 bandes
      'CB03': { fill: '#0033a0', stroke: '#222222', bands: [7, 15, 23], bandColor: '#FFD700' }, // 3 bandes
      'CEB00': { fill: '#000000', stroke: '#222222', bands: [], bandColor: '#FFD700' }, // Ceinture noire
      'CEB01': { fill: '#000000', stroke: '#222222', bands: [7], bandColor: '#FFD700' }, // 1 bande
      'CEB02': { fill: '#000000', stroke: '#222222', bands: [7, 15], bandColor: '#FFD700' }, // 2 bandes
      'CEB03': { fill: '#000000', stroke: '#222222', bands: [7, 15, 23], bandColor: '#FFD700' }, // 3 bandes
      'CEO01': { fill: '#FFA500', stroke: '#222222', bands: [7], bandColor: '#FFD700' }, // Ceinture orange
      'CEO02': { fill: '#FFA500', stroke: '#222222', bands: [7, 15], bandColor: '#FFD700' }, // 2 bandes
      'CEO03': { fill: '#FFA500', stroke: '#222222', bands: [7, 15, 23], bandColor: '#FFD700' }, // 3 bandes
      'CEV01': { fill: '#008000', stroke: '#222222', bands: [7], bandColor: '#FFD700' }, // Ceinture verte
      'CEV02': { fill: '#008000', stroke: '#222222', bands: [7, 15], bandColor: '#FFD700' }, // 2 bandes
      'CEV03': { fill: '#008000', stroke: '#222222', bands: [7, 15, 23], bandColor: '#FFD700' }, // 3 bandes
      'CJ01': { fill: '#FFD700', stroke: '#222222', bands: [7], bandColor: '#D7263D' }, // Ceinture jaune avec bande rouge
      'CJ02': { fill: '#FFD700', stroke: '#222222', bands: [7, 15], bandColor: '#D7263D' }, // 2 bandes rouges
      'CJ03': { fill: '#FFD700', stroke: '#222222', bands: [7, 15, 23], bandColor: '#D7263D' }, // 3 bandes rouges
      'MS': { fill: '#FF0000', stroke: '#FFD700', bands: [], bandColor: '#FFD700' }, // Maître Stagiaire (rouge avec bordure jaune)
      'M': { fill: '#FF0000', stroke: '#222222', bands: [], bandColor: '#0033a0' } // Maître (rouge)
    };
    
    const belt = beltColors[population] || { fill: '#666666', stroke: '#222222', bands: [] };
    
    // Dessiner le fond principal de la ceinture
    pdf.setFillColor(belt.fill);
    pdf.setDrawColor(belt.stroke);
    pdf.setLineWidth(0.3);
    
    // Rectangle principal avec coins arrondis (simulé)
    pdf.rect(x, y, width, height, 'F');
    pdf.rect(x, y, width, height); // Bordure
    
    // Dessiner les bandes (traits verticaux)
    if (belt.bands.length > 0) {
      pdf.setFillColor(belt.bandColor); // Utiliser la couleur spécifiée pour les bandes
      belt.bands.forEach(bandX => {
        const scaledX = x + (bandX / 32) * width; // Échelle depuis les coordonnées SVG
        const bandWidth = Math.max(1, width / 16); // Largeur des bandes
        pdf.rect(scaledX, y + 1, bandWidth, height - 2, 'F');
      });
    }
  }



  private static async drawImageLogo(pdf: jsPDF, imagePath: string, x: number, y: number, width: number, height: number): Promise<void> {
    try {
      // Charger l'image depuis le serveur
      const response = await fetch(imagePath);
      if (!response.ok) {
        throw new Error(`Erreur lors du chargement de l'image: ${response.status}`);
      }

      console.debug(`Chargement de l'image ${imagePath}...`);
      
      const imageBlob = await response.blob();
      
      // Convertir l'image en base64 pour jsPDF de manière synchrone
      const base64 = await this.blobToBase64(imageBlob);
      const base64Data = base64.split(',')[1];
      
      // Ajouter l'image au PDF
      pdf.addImage(base64Data, 'PNG', x, y, width, height);
      console.debug(`Image ajoutée: ${imagePath} à la position (${x}, ${y}) avec taille ${width}x${height}`);

    } catch (error) {
      console.warn(`Impossible de charger l'image ${imagePath}:`, error);
      // Fallback : dessiner un rectangle coloré
      this.drawFallbackLogo(pdf, imagePath, x, y, width, height);
    }
  }

  private static blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  private static drawFallbackLogo(pdf: jsPDF, imagePath: string, x: number, y: number, width: number, height: number): void {
    // Fallback en cas d'erreur de chargement d'image
    if (imagePath.includes('union')) {
      // Logo Union fallback
      pdf.setFillColor(this.COLORS.primary);
      pdf.rect(x, y, width, height, 'F');
      pdf.setTextColor(this.COLORS.white);
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text('UNION', x + width/2, y + height/2 + 4, { align: 'center' });
    } else if (imagePath.includes('vovinam')) {
      // Logo Vovinam fallback
      pdf.setFillColor('#FF0000');
      pdf.rect(x, y, width, height, 'F');
      pdf.setTextColor(this.COLORS.white);
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text('VOVINAM', x + width/2, y + height/2 + 4, { align: 'center' });
    }
  }

  private static drawFooter(pdf: jsPDF): void {
    const footerY = this.PAGE_HEIGHT - this.MARGIN + 20;
    const centerX = this.PAGE_WIDTH / 2;
    
    // Informations de génération simplifiées
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(this.COLORS.secondary);
    const footerText = `Généré le ${new Date().toLocaleDateString('fr-FR')} - Union Vovinam Viet Vo Dao France`;
    pdf.text(footerText, centerX, footerY, { align: 'center' });
  }

  public static async generateDirectPDF(): Promise<void> {
    console.log('DirectPDFGenerator.generateDirectPDF() appelé');
    
    try {
      // Créer le PDF
      const pdf = new jsPDF({ 
        orientation: "landscape", 
        unit: "pt", 
        format: "a4",
        compress: true // Activer la compression
      });
      
      console.log('PDF créé, chargement des données...');
      
      // Obtenir les données du calendrier (maintenant asynchrone)
      const calendarData = await this.getCalendarData();
      
      console.log('Données chargées, ajout du contenu...');
      
      // Dessiner les éléments
      await this.drawHeader(pdf);
      this.drawCalendarGrid(pdf, calendarData);
      this.drawFooter(pdf);
      
      console.log('Contenu ajouté, sauvegarde...');
      
      // Sauvegarder le PDF
      pdf.save("Calendrier_UVVVDF.pdf");
      
      console.log('PDF direct généré avec succès');
      
    } catch (error) {
      console.error('Erreur lors de la génération PDF directe:', error);
      throw error;
    }
  }

  public static async generateDirectPDFBlob(): Promise<Blob> {
    console.log('DirectPDFGenerator.generateDirectPDFBlob() appelé');
    
    try {
      const pdf = new jsPDF({ 
        orientation: "landscape", 
        unit: "pt", 
        format: "a4",
        compress: true
      });
      
      const calendarData = await this.getCalendarData();
      
      await this.drawHeader(pdf);
      this.drawCalendarGrid(pdf, calendarData);
      this.drawFooter(pdf);
      
      return new Blob([pdf.output('blob')], { type: 'application/pdf' });
      
    } catch (error) {
      console.error('Erreur lors de la génération PDF blob directe:', error);
      throw error;
    }
  }
}
